import React from "react";
import { Box, Grid, Stack } from "@mui/material";
import { useApp, useTheme } from "contexts";
import { Common, Custom, Item } from "components";
import { RadarRounded, BoltRounded, ExploreRounded } from "@mui/icons-material";
import { EnumTheme } from "utils/enums";

type Props = {};

export const Intro = (props: Props) => {
  const { t } = useApp();
  const { theme } = useTheme();
  const isDark = theme.id === EnumTheme.Dark;

  const headlineTokens = (t.intro.headline || "")
    .split("|")
    .map((token: string) => token.trim())
    .filter(Boolean);

  return (
    <Box component="section" id="intro" sx={{ width: "100%", position: "relative" }}>
      <Box sx={{ width: "100%", mt: { xs: theme.spacing.lg, md: theme.spacing.xl } }}>
        <Grid
          container
          spacing={{ xs: theme.spacing.lg, md: theme.spacing.xl }}
          alignItems="center"
        >
          {/* Left Column: Cohesive, Minimalist AAA Editorial Hierarchy */}
          <Grid item xs={12} md={7} lg={7}>
            <Stack spacing={theme.spacing.md} sx={{ position: "relative" }}>
              {/* 1. Top Telemetry Ribbon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: theme.spacing.sm,
                  flexWrap: "wrap",
                }}
              >
                {/* Pulsing Class Badge */}
                <Box
                  sx={{
                    px: theme.spacing.sm,
                    py: theme.spacing.xxs,
                    backgroundColor: theme.color.secondary.color,
                    color: theme.color.secondary.text,
                    fontFamily: '"Space Mono", monospace',
                    fontSize: theme.font.xxs,
                    fontWeight: theme.font.bold,
                    border: `1px solid ${theme.color.secondary.accent}`,
                    borderRadius: theme.border.square,
                    boxShadow: `2px 2px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      backgroundColor: theme.color.secondary.text,
                      display: "inline-block",
                      "@keyframes pulseBeacon": {
                        "0%, 100%": { opacity: 1, transform: "scale(1)" },
                        "50%": { opacity: 0.3, transform: "scale(0.8)" },
                      },
                      animation: "pulseBeacon 2s infinite ease-in-out",
                    }}
                  />
                  {t.intro.badge_role}
                </Box>

                {/* Unframed Quest Tag */}
                <Box
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: theme.font.xxs,
                    fontWeight: theme.font.semiBold,
                    color: theme.color.font.accent,
                    letterSpacing: "0.08em",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <RadarRounded sx={{ fontSize: theme.font.sm, color: theme.color.primary.color }} />
                  <span>{t.intro.badge_quest}</span>
                </Box>
              </Box>

              {/* 2. Monumental Hero Display Title & Unified Monospace Role Line */}
              <Box>
                <Custom.Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: {
                      xs: theme.font.xl,
                      sm: theme.font.xxl,
                      md: theme.font.xxxl,
                    },
                    fontWeight: theme.font.extraBold,
                    lineHeight: 1.08,
                    letterSpacing: "-0.025em",
                    display: "block",
                    color: theme.color.font.color,
                  }}
                >
                  {t.intro.header}
                </Custom.Typography>

                {/* Clean Unframed Role Headline */}
                <Box
                  component="h2"
                  sx={{
                    m: 0,
                    mt: theme.spacing.xs,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: theme.spacing.xs,
                    fontFamily: '"Space Mono", monospace',
                    fontSize: {
                      xs: theme.font.xs,
                      sm: theme.font.sm,
                      md: theme.font.md,
                    },
                    fontWeight: theme.font.medium,
                    color: theme.color.primary.color,
                    letterSpacing: "0.02em",
                  }}
                >
                  {headlineTokens.map((token: string, idx: number) => (
                    <React.Fragment key={idx}>
                      {idx > 0 && (
                        <Box
                          component="span"
                          sx={{
                            color: theme.color.border.accent,
                            fontWeight: theme.font.light,
                            userSelect: "none",
                          }}
                        >
                          /
                        </Box>
                      )}
                      <Box component="span">{token}</Box>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>

              {/* 3. Harmonic Editorial Pitch & Supporting Narrative */}
              <Stack spacing={theme.spacing.sm} sx={{ maxWidth: 640, pt: theme.spacing.xxs }}>
                {/* Lead Statement: Elevated Scale & Medium Weight for Effortless Authority */}
                <Custom.Typography
                  variant="body1"
                  component="p"
                  sx={{
                    fontSize: {
                      xs: theme.font.md,
                      md: theme.font.lg,
                    },
                    fontWeight: theme.font.medium,
                    lineHeight: 1.45,
                    letterSpacing: "-0.01em",
                    color: theme.color.font.color,
                  }}
                >
                  {t.intro.lead}
                </Custom.Typography>

                {/* Supporting Narrative: Calm, Balanced Body Copy */}
                <Custom.Typography
                  variant="body2"
                  component="p"
                  sx={{
                    color: theme.color.font.accent,
                    fontSize: theme.font.sm,
                    fontWeight: theme.font.normal,
                    lineHeight: 1.7,
                  }}
                >
                  {t.intro.sub}
                </Custom.Typography>

                {/* Tagline: Subtle, Understated Closing Note */}
                <Custom.Typography
                  variant="body2"
                  component="p"
                  sx={{
                    color: theme.color.font.accent,
                    fontSize: theme.font.xs,
                    fontWeight: theme.font.light,
                    fontStyle: "italic",
                    lineHeight: 1.6,
                    opacity: 0.85,
                  }}
                >
                  {t.intro.tagline}
                </Custom.Typography>
              </Stack>

              {/* 4. Primary Action Controls */}
              <Stack
                direction="row"
                spacing={theme.spacing.md}
                sx={{
                  pt: theme.spacing.xs,
                  flexWrap: "wrap",
                  gap: theme.spacing.sm,
                }}
              >
                <Custom.Button
                  onClick={() => {
                    const contact = document.getElementById("contact");
                    if (contact) contact.scrollIntoView({ behavior: "smooth" });
                  }}
                  sx={{
                    px: theme.spacing.lg,
                    py: theme.spacing.sm,
                    fontSize: theme.font.sm,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing.xs }}>
                    <BoltRounded sx={{ fontSize: theme.font.md }} />
                    <span>{t.intro.cta_contact}</span>
                  </Box>
                </Custom.Button>

                <Custom.Button
                  secondary
                  onClick={() => {
                    const showcase = document.getElementById("project");
                    if (showcase) showcase.scrollIntoView({ behavior: "smooth" });
                  }}
                  sx={{
                    px: theme.spacing.lg,
                    py: theme.spacing.sm,
                    fontSize: theme.font.sm,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing.xs }}>
                    <ExploreRounded sx={{ fontSize: theme.font.md }} />
                    <span>{t.intro.cta_projects}</span>
                  </Box>
                </Custom.Button>
              </Stack>
            </Stack>
          </Grid>

          {/* Right Column: Adaptive Hero Dossier / Operator Card */}
          <Grid item xs={12} md={5} lg={5}>
            <Common.Parallax />
          </Grid>
        </Grid>

        {/* Attribute Pedestals leveraging Item.Metric */}
        <Grid
          container
          spacing={theme.spacing.lg}
          sx={{ pt: theme.spacing.xl }}
        >
          <Grid item xs={12} sm={4}>
            <Item.Metric
              label={t.intro.attr1_label}
              value={t.intro.attr1_value}
              description={t.intro.attr1_desc}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Item.Metric
              label={t.intro.attr2_label}
              value={t.intro.attr2_value}
              description={t.intro.attr2_desc}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Item.Metric
              label={t.intro.attr3_label}
              value={t.intro.attr3_value}
              description={t.intro.attr3_desc}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};